import { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState({}); // storeSlug -> {items: [...], storeId}
  const [pincode, setPincode] = useState(localStorage.getItem("qd_pincode") || "");
  const [selectedAddress, setSelectedAddressState] = useState(() => {
    const s = localStorage.getItem("qd_selected_address");
    return s ? JSON.parse(s) : null;
  });

  useEffect(() => {
    const saved = localStorage.getItem("qd_cart");
    if (saved) setCart(JSON.parse(saved));
  }, []);

  const persist = (next) => {
    setCart(next);
    localStorage.setItem("qd_cart", JSON.stringify(next));
  };

  const setStorePincode = (pin) => {
    setPincode(pin);
    localStorage.setItem("qd_pincode", pin);
  };

  const setSelectedAddress = (addr) => {
    setSelectedAddressState(addr);
    if (addr) {
      localStorage.setItem("qd_selected_address", JSON.stringify(addr));
      // Keep pincode in sync with the chosen address
      setStorePincode(addr.pincode);
    } else {
      localStorage.removeItem("qd_selected_address");
    }
  };

  const add = (storeSlug, storeId, product) => {
    const next = { ...cart };
    if (!next[storeSlug]) next[storeSlug] = { storeId, items: [] };
    // Clear other store carts (single-store cart)
    Object.keys(next).forEach((k) => { if (k !== storeSlug) delete next[k]; });
    const items = [...next[storeSlug].items];
    const idx = items.findIndex((i) => i.product_id === product.id);
    if (idx >= 0) items[idx].qty += 1;
    else items.push({
      product_id: product.id, name: product.name,
      price: product.price, qty: 1,
      image: product.images?.[0] || ""
    });
    next[storeSlug] = { storeId, items };
    persist(next);
  };

  const decrement = (storeSlug, productId) => {
    const next = { ...cart };
    if (!next[storeSlug]) return;
    const items = next[storeSlug].items
      .map((i) => i.product_id === productId ? { ...i, qty: i.qty - 1 } : i)
      .filter((i) => i.qty > 0);
    if (items.length === 0) delete next[storeSlug];
    else next[storeSlug] = { ...next[storeSlug], items };
    persist(next);
  };

  const remove = (storeSlug, productId) => {
    const next = { ...cart };
    if (!next[storeSlug]) return;
    next[storeSlug].items = next[storeSlug].items.filter((i) => i.product_id !== productId);
    if (next[storeSlug].items.length === 0) delete next[storeSlug];
    persist(next);
  };

  const clear = (storeSlug) => {
    const next = { ...cart };
    delete next[storeSlug];
    persist(next);
  };

  const getStoreCart = (slug) => cart[slug] || { items: [] };
  const itemCount = (slug) => (cart[slug]?.items || []).reduce((s, i) => s + i.qty, 0);
  const subtotal = (slug) => (cart[slug]?.items || []).reduce((s, i) => s + i.qty * i.price, 0);

  // Active cart = the single store currently in the cart (single-store policy)
  const activeSlug = Object.keys(cart).find((k) => cart[k]?.items?.length > 0) || null;
  const activeCart = activeSlug ? {
    slug: activeSlug,
    storeId: cart[activeSlug].storeId,
    items: cart[activeSlug].items,
    count: cart[activeSlug].items.reduce((s, i) => s + i.qty, 0),
    total: cart[activeSlug].items.reduce((s, i) => s + i.qty * i.price, 0),
  } : null;

  return (
    <CartContext.Provider value={{
      cart, add, decrement, remove, clear,
      getStoreCart, itemCount, subtotal,
      activeCart,
      pincode, setPincode: setStorePincode,
      selectedAddress, setSelectedAddress,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
